import React, { useState, useMemo, useEffect } from 'react';
import { db, storage } from '../../firebase/config';
import { doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { ref, deleteObject } from 'firebase/storage';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import DocumentItem from '../common/DocumentItem';
import Accordion from '../common/Accordion';
import DateCard from '../common/DateCard';
import FilterBar from '../common/FilterBar';
import { ClientGrid } from './DataEntryDashboard.styles';
import { 
  ViewContainer, 
  ViewHeader, 
  BackButton, 
  ClientNameTitle 
} from './ClientFilesView.styles';
import { ArrowLeft } from 'lucide-react';

const ClientFilesView = ({ client, documents, onBack }) => {
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [openAccordion, setOpenAccordion] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const handleUpdateStatus = async (id, status) => {
    const docRef = doc(db, 'documents', id);
    try {
      await updateDoc(docRef, { status });
      toast.success(`Document status updated!`);
    } catch (error) {
      toast.error("Failed to update status.");
    }
  };

  const handleDelete = async (docToDelete) => {
    if (!window.confirm(`Are you sure you want to permanently delete ${docToDelete.fileName}?`)) {
      return;
    }
    const toastId = toast.loading("Deleting document...");
    try {
      const fileRef = ref(storage, docToDelete.filePath);
      await deleteObject(fileRef);
      await deleteDoc(doc(db, 'documents', docToDelete.id));
      toast.success("Document deleted permanently.", { id: toastId });
    } catch (error) {
      if (error.code === 'storage/object-not-found') {
        await deleteDoc(doc(db, 'documents', docToDelete.id));
        toast.success("DB record deleted.", { id: toastId });
      } else {
        toast.error("Failed to delete document.", { id: toastId });
        console.error("Delete error:", error);
      }
    }
  };

  const handleAccordionToggle = (category) => {
    setOpenAccordion(prevOpen => (prevOpen === category ? null : category));
  };

  const docsByMonth = useMemo(() => {
    const filtered = documents.filter(doc => {
      const docDate = doc.uploadedAt.toDate();
      const matchesStatus = statusFilter === 'All' || doc.status === statusFilter;
      const matchesStartDate = !startDate || docDate >= startDate;
      const matchesEndDate = !endDate || docDate < new Date(endDate.getTime() + 24 * 60 * 60 * 1000);
      return matchesStatus && matchesStartDate && matchesEndDate;
    });
    return filtered.reduce((acc, doc) => {
      const monthYear = format(doc.uploadedAt.toDate(), 'MMMM yyyy');
      if (!acc[monthYear]) acc[monthYear] = [];
      acc[monthYear].push(doc);
      return acc;
    }, {});
  }, [documents, statusFilter, startDate, endDate]);

  const docsByCatInSelectedMonth = useMemo(() => {
    if (!selectedMonth || !docsByMonth[selectedMonth]) return {};
    const filtered = docsByMonth[selectedMonth].filter(doc => {
      const matchesSearch = doc.fileName.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter === 'All' || doc.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
    return filtered.reduce((acc, doc) => {
      const category = doc.category;
      if (!acc[category]) acc[category] = [];
      acc[category].push(doc);
      return acc;
    }, {});
  }, [selectedMonth, docsByMonth, searchTerm, statusFilter]);

  useEffect(() => {
    if (selectedMonth) {
        const categories = Object.keys(docsByCatInSelectedMonth);
        setOpenAccordion(categories.length > 0 ? categories[0] : null);
    }
  }, [selectedMonth, docsByCatInSelectedMonth]);


  if (selectedMonth) {
    return (
      <ViewContainer>
        <ViewHeader>
          <BackButton onClick={() => setSelectedMonth(null)}><ArrowLeft size={20} /></BackButton>
          <ClientNameTitle>{client.clientName} - {selectedMonth}</ClientNameTitle>
        </ViewHeader>
        
        {/* --- FIX: Use the full FilterBar here, hiding only the date inputs --- */}
        <FilterBar
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            hideDateFilters={true} 
        />
        
        {Object.keys(docsByCatInSelectedMonth).length > 0 ? (
          Object.keys(docsByCatInSelectedMonth).map(category => (
            <Accordion 
              key={category} 
              title={`${category} (${docsByCatInSelectedMonth[category].length})`}
              isOpen={openAccordion === category}
              onToggle={() => handleAccordionToggle(category)}
            >
              {docsByCatInSelectedMonth[category].map(doc => (
                <DocumentItem
                  key={doc.id}
                  document={doc}
                  userRole="data-entry"
                  onUpdateStatus={handleUpdateStatus}
                  onDelete={handleDelete}
                />
              ))}
            </Accordion>
          ))
        ) : (
          <p>No documents found for this month with the current filters.</p>
        )}
      </ViewContainer>
    );
  }

  // Default view showing DateCards
  return (
    <ViewContainer>
      <ViewHeader>
        <BackButton onClick={onBack}><ArrowLeft size={20} /></BackButton>
        <ClientNameTitle>{client.clientName}'s Files</ClientNameTitle>
      </ViewHeader>

      <FilterBar
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
        hideDateFilters={false}
      />

      <ClientGrid>
        {Object.keys(docsByMonth).length > 0 ? (
          Object.keys(docsByMonth).map(monthYear => {
            const monthDocs = docsByMonth[monthYear];
            const stats = {
              total: monthDocs.length,
              pending: monthDocs.filter(d => d.status === 'Pending').length,
            };
            if (stats.total > 0) {
              return (
                <DateCard 
                  key={monthYear}
                  monthYear={monthYear}
                  stats={stats}
                  onClick={() => {
                    setSelectedMonth(monthYear);
                    setSearchTerm(''); 
                    setStatusFilter('All');
                  }}
                />
              );
            }
            return null;
          })
        ) : (
          <p>No documents found for the selected filters.</p>
        )}
      </ClientGrid>
    </ViewContainer>
  );
};

export default ClientFilesView;